import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { PosterCard } from "@/components/rawan/PosterCard";
import { useAllItems } from "@/lib/library-store";
import { useTheme } from "@/lib/theme-context";
import { COUNTRIES, matchesCountry, getCountryByCode } from "@/lib/countries";

type Search = { country?: string };

export const Route = createFileRoute("/series/")({
  validateSearch: (s: Record<string, unknown>): Search => {
    const c = typeof s.country === "string" ? s.country.toUpperCase() : undefined;
    return c && COUNTRIES.some((x) => x.code === c) ? { country: c } : {};
  },
  loaderDeps: ({ search }) => ({ country: search.country }),
  loader: ({ deps }) => ({ country: deps.country }),
  head: ({ loaderData }) => {
    const c = loaderData?.country ? getCountryByCode(loaderData.country) : undefined;
    const titleAr = c ? `مسلسلات ${c.ar} — Rawan UI` : "مسلسلات — Rawan UI";
    const titleEn = c ? `${c.en} Series — Rawan UI` : "Series — Rawan UI";
    const descAr = c ? `أحدث المسلسلات ${c.ar} بمواسم وحلقات متجددة.` : "مسلسلات حصرية بأحدث المواسم والحلقات.";
    const descEn = c ? `Latest ${c.en} series with up-to-date seasons and episodes.` : "Exclusive series with the latest seasons and episodes.";
    const url = c ? `/series?country=${c.code}` : "/series";
    return {
      meta: [
        { title: titleAr },
        { name: "description", content: descAr },
        { property: "og:title", content: titleAr },
        { property: "og:description", content: descAr },
        { property: "og:url", content: url },
        { name: "twitter:title", content: titleEn },
        { name: "twitter:description", content: descEn },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "x-default", href: "/series" },
        { rel: "alternate", hrefLang: "ar", href: c ? `/series?lang=ar&country=${c.code}` : "/series?lang=ar" },
        { rel: "alternate", hrefLang: "en", href: c ? `/series?lang=en&country=${c.code}` : "/series?lang=en" },
        { rel: "alternate", hrefLang: "fr", href: c ? `/series?lang=fr&country=${c.code}` : "/series?lang=fr" },
      ],
    };
  },
  component: SeriesPage,
});

function SeriesPage() {
  const { country } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { dir, language } = useTheme();
  const isAr = dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const all = useAllItems().filter((m) => m.type === "series");
  const series = country ? all.filter((m) => matchesCountry(m.country, country)) : all;

  const label = (c: { ar: string; en: string; fr: string }) =>
    language === "ar" ? c.ar : language === "fr" ? c.fr : c.en;

  const setCountry = (code: string | null) =>
    navigate({ search: code ? { country: code } : {}, replace: false } as any);

  const activeCountry = country ? getCountryByCode(country) : undefined;

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-10">
        <header className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold">
            {activeCountry
              ? language === "ar" ? `مسلسلات ${activeCountry.ar}` : language === "fr" ? `Séries ${activeCountry.fr}` : `${activeCountry.en} Series`
              : t("\u062c\u0645\u064a\u0639 \u0627\u0644\u0645\u0633\u0644\u0633\u0644\u0627\u062a", "All Series", "Toutes les séries")}
          </h1>
          <p className="text-muted-foreground mt-1">{t("\u062a\u0627\u0628\u0639 \u0622\u062e\u0631 \u0627\u0644\u0645\u0648\u0627\u0633\u0645 \u0648\u0627\u0644\u062d\u0644\u0642\u0627\u062a", "Catch up with the latest seasons", "Suivez les dernières saisons et épisodes")}</p>
        </header>

        <div className="mb-8 -mx-4 px-4 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max sm:flex-wrap sm:min-w-0">
            <button
              onClick={() => setCountry(null)}
              className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-xs sm:text-sm border transition ${
                !country ? "bg-foreground text-background border-foreground font-bold" : "bg-card hover:bg-accent border-border"
              }`}
            >
              {t("\u0627\u0644\u0643\u0644", "All", "Tous")}
            </button>
            {COUNTRIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCountry(c.code)}
                className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-xs sm:text-sm border inline-flex items-center gap-1.5 transition ${
                  country === c.code ? "bg-foreground text-background border-foreground font-bold" : "bg-card hover:bg-accent border-border"
                }`}
              >
                <span aria-hidden>{c.flag}</span>
                <span>{label(c)}</span>
              </button>
            ))}
          </div>
        </div>

        {series.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-border rounded-2xl bg-card">
            <p className="text-base font-semibold mb-1">
              {t("\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u0633\u0644\u0633\u0644\u0627\u062a \u062a\u0637\u0627\u0628\u0642 \u0647\u0630\u0627 \u0627\u0644\u062a\u0635\u0646\u064a\u0641 \u0628\u0639\u062f.", "No series match this filter yet.", "Aucune série ne correspond à ce filtre.")}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {t("\u062c\u0631\u0651\u0628 \u0627\u062e\u062a\u064a\u0627\u0631 \u062f\u0648\u0644\u0629 \u0623\u062e\u0631\u0649 \u0623\u0648 \u0639\u0631\u0636 \u0627\u0644\u0643\u0644.", "Try another country or view all titles.", "Essayez un autre pays ou affichez tout.")}
            </p>
            <button
              onClick={() => setCountry(null)}
              className="btn-primary inline-flex items-center gap-2 !py-2 !px-4 text-sm"
            >
              {t("\u0639\u0631\u0636 \u0643\u0644 \u0627\u0644\u0645\u0633\u0644\u0633\u0644\u0627\u062a", "Show all series", "Afficher toutes les séries")}
            </button>
          </div>
        ) : (
          <><h2 className="sr-only">{t("\u0627\u0644\u0646\u062a\u0627\u0626\u062c", "Results", "Résultats")}</h2><div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {series.map((m) => <PosterCard key={m.id} movie={m} />)}
          </div></>
        )}
      </main>
      <Footer />
    </div>
  );
}

